import { useContext } from "react";
import {
  Anchor,
  Badge,
  Card,
  Grid,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { FEATURE_PACKAGES } from "@congrs/schemas";
import { useGetAllFeatureFlags } from "../../../../api/featureFlags";
import { AuthContext } from "../../../../services/context";
import { getCurrentWorkspace } from "../../../../services/userConfig";
import { getFeatureStyle, getPackageColor } from "../../featureStyles";
import FeatureCard from "./FeatureCard";

const SUPPORT_EMAIL = "support@congrs.com";

function PackageFieldCard({ field, value }) {
  const { icon: Icon, color } = getFeatureStyle(field.name);

  return (
    <Card shadow="sm" radius="md" padding="lg" withBorder>
      <Group justify="space-between" align="center" wrap="nowrap" h="100%">
        <Stack
          gap="md"
          justify="space-between"
          style={{ flex: 1, alignSelf: "stretch" }}
        >
          <Group gap="md" wrap="nowrap" align="flex-start">
            <ThemeIcon size="xl" radius="md" variant="filled" color={color}>
              <Icon size={22} />
            </ThemeIcon>
            <Stack gap={4}>
              <Text fw={600} size="md">
                {field.label}
              </Text>
              <Text size="sm" c="dimmed" lh={1.4}>
                {field.description}
              </Text>
            </Stack>
          </Group>

          <Anchor href={`mailto:${SUPPORT_EMAIL}`} size="xs">
            Contact us to upgrade
          </Anchor>
        </Stack>

        <Badge
          variant="light"
          color={color}
          size="lg"
          style={{ flexShrink: 0 }}
        >
          {value}
        </Badge>
      </Group>
    </Card>
  );
}

function PackageSection({ pkg, cards }) {
  const accent = getPackageColor(pkg.key);

  return (
    <Paper
      radius="md"
      p="lg"
      h="100%"
      style={{ backgroundColor: `${accent}14` }}
    >
      <Title order={3} mb={4}>
        {pkg.label}
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        {pkg.description}
      </Text>

      <SimpleGrid cols={{ base: 1, sm: cards.length > 1 ? 2 : 1 }} spacing="md">
        {cards}
      </SimpleGrid>
    </Paper>
  );
}

function buildPackageCards(pkg, flags, event) {
  const flagCards = pkg.flags
    .map((flag) => flags.find((f) => f.featureName === flag))
    .filter(Boolean)
    .map((feature) => <FeatureCard key={feature.id} feature={feature} />);

  const fieldCards = (pkg.fields ?? [])
    .filter((field) => event?.[field.name] != null)
    .map((field) => (
      <PackageFieldCard
        key={field.name}
        field={field}
        value={event[field.name]}
      />
    ));

  return [...flagCards, ...fieldCards];
}

export default function FeatureTab({ event }) {
  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);
  const { data } = useGetAllFeatureFlags({ eventId });

  const flags = data ?? [];

  const sections = Object.values(FEATURE_PACKAGES)
    .map((pkg) => ({ pkg, cards: buildPackageCards(pkg, flags, event) }))
    .filter(({ cards }) => cards.length > 0);

  return (
    <Grid p="md" gutter="md">
      {sections.map(({ pkg, cards }) => (
        <Grid.Col
          key={pkg.key}
          span={{ base: 12, md: cards.length > 1 ? 12 : 6 }}
        >
          <PackageSection pkg={pkg} cards={cards} />
        </Grid.Col>
      ))}
    </Grid>
  );
}
